import { useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, Checkbox } from "@material-ui/core";
import { IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ToolkitTable from "components/Cards/Tables/ToolkitTable";

import { setSelectedProducts } from "redux/reducers/criarVitrineReducer";

import { buildShowcaseProductGradeColumns } from "../../../shared/utils/product-grade-fields";
import BulkSelectionModal from "../../../shared/components/bulk-selection-modal";

export default function ProductsTable({
  totalSize,
  currentPage,
  currentSizePerPage,
  setProductsTablePagination,
}) {
  const dispatch = useDispatch();

  const selectedProducts = useSelector(
    (state) => state.criarVitrine.selectedProducts,
  );
  const editMode = useSelector((state) => state.criarVitrine.editMode);
  const originalStatus = useSelector(
    (state) => state.criarVitrine.originalStatus,
  );
  const originalSelectedProductCodes = useSelector(
    (state) => state.criarVitrine.originalSelectedProductCodes,
  );

  const lockedCodes = useMemo(() => {
    if (!editMode || originalStatus !== "PUBLICADO") return new Set();
    return new Set(originalSelectedProductCodes);
  }, [editMode, originalStatus, originalSelectedProductCodes]);

  const [markedForRemoval, setMarkedForRemoval] = useState([]);
  const [openSelectAllDialog, setOpenSelectAllDialog] = useState(false);

  const handleDelete = useCallback(
    (codigo) => {
      if (lockedCodes.has(codigo)) return;
      dispatch(
        setSelectedProducts(
          selectedProducts.filter((p) => p.Codigo !== codigo),
        ),
      );
      setMarkedForRemoval((prev) => prev.filter((c) => c !== codigo));
    },
    [dispatch, selectedProducts, lockedCodes],
  );

  const currentPageItems = useMemo(() => {
    const start = currentPage * currentSizePerPage;
    return selectedProducts.slice(start, start + currentSizePerPage);
  }, [selectedProducts, currentPage, currentSizePerPage]);

  const toggleMarked = useCallback(
    (codigo) => {
      if (lockedCodes.has(codigo)) return;
      setMarkedForRemoval((prev) =>
        prev.includes(codigo)
          ? prev.filter((c) => c !== codigo)
          : [...prev, codigo],
      );
    },
    [lockedCodes],
  );

  const removableCurrentPageItems = useMemo(
    () => currentPageItems.filter((item) => !lockedCodes.has(item.Codigo)),
    [currentPageItems, lockedCodes],
  );

  const allPageMarked =
    removableCurrentPageItems.length > 0 &&
    removableCurrentPageItems.every((item) =>
      markedForRemoval.includes(item.Codigo),
    );

  const markPage = useCallback(() => {
    setMarkedForRemoval((prev) => [
      ...prev,
      ...removableCurrentPageItems
        .map((item) => item.Codigo)
        .filter((codigo) => !prev.includes(codigo)),
    ]);
  }, [removableCurrentPageItems]);

  const markAll = useCallback(() => {
    setMarkedForRemoval(
      selectedProducts
        .filter((item) => !lockedCodes.has(item.Codigo))
        .map((item) => item.Codigo),
    );
  }, [selectedProducts, lockedCodes]);

  const unmarkPage = useCallback(() => {
    setMarkedForRemoval((prev) =>
      prev.filter((c) => !currentPageItems.some((item) => item.Codigo === c)),
    );
  }, [currentPageItems]);

  const unmarkAll = useCallback(() => {
    setMarkedForRemoval([]);
  }, []);

  const handleRemoveMarked = useCallback(() => {
    dispatch(
      setSelectedProducts(
        selectedProducts.filter((p) => !markedForRemoval.includes(p.Codigo)),
      ),
    );
    setMarkedForRemoval([]);
    setOpenSelectAllDialog(false);
  }, [dispatch, selectedProducts, markedForRemoval]);

  const columnModel = useMemo(() => {
    const baseColumns = buildShowcaseProductGradeColumns({ includeDescricao: true });
    return [
      {
        dataField: "marked",
        text: "",
        align: "center",
        headerAlign: "center",
        formatter: (_, row) => {
          const locked = lockedCodes.has(row.Codigo);
          const checkbox = (
            <Checkbox
              checked={markedForRemoval.includes(row.Codigo)}
              onChange={() => toggleMarked(row.Codigo)}
              disabled={locked}
            />
          );
          return locked ? (
            <Tooltip title="Não é possível remover produto de vitrine já publicada">
              <span>{checkbox}</span>
            </Tooltip>
          ) : (
            checkbox
          );
        },
        headerFormatter: () => (
          <Checkbox
            checked={allPageMarked}
            indeterminate={
              removableCurrentPageItems.some((item) =>
                markedForRemoval.includes(item.Codigo),
              ) && !allPageMarked
            }
            onChange={() => setOpenSelectAllDialog(true)}
            disabled={removableCurrentPageItems.length === 0}
          />
        ),
      },
      ...baseColumns,
      {
        dataField: "actions",
        text: "",
        align: "center",
        headerAlign: "center",
        formatter: (_, row) => {
          const locked = lockedCodes.has(row.Codigo);
          const button = (
            <IconButton
              onClick={() => handleDelete(row.Codigo)}
              size="small"
              disabled={locked}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          );
          return locked ? (
            <Tooltip title="Não é possível remover produto de vitrine já publicada">
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        },
      },
    ];
  }, [
    allPageMarked,
    removableCurrentPageItems,
    handleDelete,
    markedForRemoval,
    toggleMarked,
    lockedCodes,
  ]);

  return (
    <Box style={{ minHeight: "450px" }}>
      <ToolkitTable
        key={selectedProducts.length}
        keyField="Codigo"
        dataTable={selectedProducts}
        columnModel={columnModel}
        hideEmptyMessages
        pagination={{
          options: [10, 25, 50, 100],
          defaultOption: 10,
          showTotal: true,
          totalSize: totalSize,
          page: currentPage,
          sizePerPage: currentSizePerPage,
        }}
        bootstrapTableProps={{
          onTableChange: (type, { page, sizePerPage }) => {
            if (type === "pagination") {
              setProductsTablePagination({ page, sizePerPage });
            }
          },
        }}
        allowEmpty
      />
      <BulkSelectionModal
        open={openSelectAllDialog}
        onClose={() => setOpenSelectAllDialog(false)}
        title="Selecionar produtos para remover"
        entityLabel="produtos"
        pageCount={currentPageItems.length}
        pageSelectedCount={
          currentPageItems.filter((item) =>
            markedForRemoval.includes(item.Codigo),
          ).length
        }
        totalCount={selectedProducts.length}
        totalSelectedCount={markedForRemoval.length}
        onSelectPage={markPage}
        onSelectAll={markAll}
        onDeselectPage={unmarkPage}
        onDeselectAll={unmarkAll}
        confirmAction={{
          label: `Remover (${markedForRemoval.length})`,
          icon: <DeleteOutlineIcon fontSize="small" />,
          onClick: handleRemoveMarked,
          disabled: markedForRemoval.length === 0,
        }}
      />
    </Box>
  );
}
